import React, { useState, useEffect } from 'react';
import { adminApi } from '@/services/api';
import { Plus, Pencil, Trash2, Save, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploadCrop from '@/components/admin/ImageUploadCrop';
import { resolveImage, SERVICE_IMAGE_BASE } from '@/lib/imageUrl';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nombre: '', descripcion: '', ruta_img: '', orden: 99, activo: true });

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      const res = await adminApi.getServices();
      setServices(res.data);
    } catch { toast.error('Error al cargar servicios'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ nombre: '', descripcion: '', ruta_img: '', orden: 99, activo: true });
    setEditingId(null);
    setShowCreate(false);
  };

  const startEdit = (service) => {
    setForm({ nombre: service.nombre, descripcion: service.descripcion, ruta_img: service.ruta_img, orden: service.orden, activo: service.activo });
    setEditingId(service.id);
    setShowCreate(false);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingId) {
        await adminApi.updateService(editingId, form);
        toast.success('Servicio actualizado');
      } else {
        await adminApi.createService(form);
        toast.success('Servicio creado');
      }
      resetForm();
      loadServices();
    } catch { toast.error('Error al guardar servicio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    try {
      await adminApi.deleteService(id);
      toast.success('Servicio eliminado');
      loadServices();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="p-6" data-testid="admin-services-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-steel-dark uppercase tracking-wider">Servicios</h1>
          <p className="text-steel-gray mt-1">Gestiona los servicios de transformación</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="create-service-btn"
        >
          <Plus size={18} /> Nuevo Servicio
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreate || editingId) && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6" data-testid="service-form">
          <h3 className="font-bold text-lg text-steel-dark mb-4">
            {editingId ? 'Editar Servicio' : 'Nuevo Servicio'}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Nombre *</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="service-name-input"
              />
            </div>
            <ImageUploadCrop
              label="Imagen"
              value={form.ruta_img}
              onChange={(path) => setForm({ ...form, ruta_img: path })}
              folder="services"
              legacyBase={SERVICE_IMAGE_BASE}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-steel-gray mb-1">Descripción</label>
              <textarea
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="service-desc-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Orden</label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="service-order-input"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  className="w-4 h-4 accent-accent-primary"
                />
                <span className="text-sm text-steel-gray">Activo</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="btn-primary flex items-center gap-2" data-testid="service-save-btn">
              <Save size={16} /> {editingId ? 'Actualizar' : 'Crear'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-steel-gray hover:text-steel-dark transition-colors flex items-center gap-2" data-testid="service-cancel-btn">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Services Table */}
      {loading ? (
        <div className="text-center py-12 text-steel-gray">Cargando...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full" data-testid="services-table">
            <thead className="bg-steel-dark text-white text-left">
              <tr>
                <th className="px-4 py-3 text-sm font-medium w-12">#</th>
                <th className="px-4 py-3 text-sm font-medium w-20">Imagen</th>
                <th className="px-4 py-3 text-sm font-medium">Nombre</th>
                <th className="px-4 py-3 text-sm font-medium hidden md:table-cell">Descripción</th>
                <th className="px-4 py-3 text-sm font-medium w-20">Estado</th>
                <th className="px-4 py-3 text-sm font-medium w-28">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {services.map(service => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-steel-gray">
                    <GripVertical size={14} className="inline mr-1" />{service.orden}
                  </td>
                  <td className="px-4 py-3">
                    <img src={resolveImage(service.ruta_img, SERVICE_IMAGE_BASE) || 'https://placehold.co/56x40/1A1A2E/E63946?text=S'} alt="" className="w-14 h-10 rounded object-cover" onError={(e) => { e.target.src = 'https://placehold.co/56x40/1A1A2E/E63946?text=S'; }} />
                  </td>
                  <td className="px-4 py-3 font-medium text-steel-dark">{service.nombre}</td>
                  <td className="px-4 py-3 text-sm text-steel-gray hidden md:table-cell">
                    {service.descripcion?.substring(0, 80)}{service.descripcion?.length > 80 ? '...' : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${service.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {service.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(service)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-service-${service.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(service.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-service-${service.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {services.length === 0 && (
            <div className="text-center py-12 text-steel-gray">No hay servicios registrados</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminServices;
